import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { IdeaSource } from "../types";
// #region: Styled Components
const ColoredRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== "source",
})(({ theme, source }) => ({
    borderLeft: source
        ? `4px solid ${source === IdeaSource.HUMAN ? theme.palette.warning.main : theme.palette.primary.main}`
        : "4px solid transparent",
    "& td": {
        paddingLeft: 12,
    },
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.background.default,
    },
}));
const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: "center",
    padding: 40,
    color: theme.palette.text.secondary,
    fontStyle: "italic",
}));
const FeedbackCell = styled(TableCell)(({ theme }) => ({
    maxWidth: 250,
    wordBreak: "break-word",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
}));
const HeaderCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
}));
// Out of alphabetical order since it extends HeaderCell
const CenteredHeaderCell = styled(HeaderCell)({
    textAlign: "center",
});
const IdeaCell = styled(TableCell)({
    maxWidth: 300,
    wordBreak: "break-word",
    lineHeight: 1.4,
});
const IndexCell = styled(TableCell)(({ theme }) => ({
    textAlign: "center",
    fontWeight: 600,
    color: theme.palette.text.primary,
    width: 64,
}));
const IndexHeaderCell = styled(HeaderCell)({
    width: 64,
});
const ScoreCell = styled(TableCell)(({ theme }) => ({
    textAlign: "center",
    fontWeight: 600,
    color: theme.palette.text.primary,
}));
// #endregion: Types
const IdeasTable = ({ data }) => {
    if (!data?.ideas.length || !data?.objectives) {
        return _jsx(EmptyState, { children: "No ideas yet." });
    }
    // TODO: Remove when we go to es2023
    // eslint-disable-next-line unicorn/no-array-reverse
    const reversedIdeas = [...data.ideas].reverse();
    return (_jsx(TableContainer, { component: Paper, elevation: 1, children: _jsxs(Table, { "aria-label": "Ideas table", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(IndexHeaderCell, { children: "#" }), _jsx(HeaderCell, { children: "Idea" }), _jsx(CenteredHeaderCell, { children: data.objectives.objective1 || "Objective 1 score" }), _jsx(CenteredHeaderCell, { children: data.objectives.objective2 || "Objective 2 score" }), _jsx(HeaderCell, { children: "Feedback" })] }) }), _jsx(TableBody, { children: reversedIdeas.map((idea, index) => {
                        const ideaNumber = data.ideas.length - index;
                        return (_jsxs(ColoredRow, { "data-testid": `ideas_table-table_body_row_${index}_tr`, hover: true, source: idea.source, children: [_jsx(IndexCell, { children: ideaNumber }), _jsx(IdeaCell, { children: idea.idea }), _jsx(ScoreCell, { children: idea.score1.toFixed(1) }), _jsx(ScoreCell, { children: idea.score2.toFixed(1) }), _jsx(FeedbackCell, { children: idea.feedback })] }, idea.id));
                    }) })] }) }));
};
export default IdeasTable;
